import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import Icon from './Icon';
import '../css/sort-menu.css';

const ComplaintsSortFiltersMenu = ({
  sort,
  direction,
  onSortChange,
  onDirectionChange,
  status,
  onStatusChange,
  targetType,
  targetTypeOptions,
  onTargetTypeChange,
  onReset,
  translationKey = 'complaints'
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showExtraFilters, setShowExtraFilters] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const sortOptions = [
    { value: 'createdAt', label: t('orders.filters.sortByCreatedDate') },
  ];

  const directionOptions = [
    { value: 'DESC', label: t('orders.filters.descending'), icon: '↓' },
    { value: 'ASC', label: t('orders.filters.ascending'), icon: '↑' }
  ];

  const handleSortSelect = (value) => {
    onSortChange(value);
    setIsOpen(false);
  };

  const handleDirectionSelect = (value) => {
    onDirectionChange(value);
    setIsOpen(false);
  };

  const handleStatusToggle = (val) => {
    const cur = status || [];
    const next = cur.includes(val) ? cur.filter(s => s !== val) : [...cur, val];
    onStatusChange(next);
  };

  const statusOptionsList = [
    { value: 'PENDING', label: t('complaints.statuses.PENDING') },
    { value: 'IN_REVIEW', label: t('complaints.statuses.IN_REVIEW') },
    { value: 'RESOLVED', label: t('complaints.statuses.RESOLVED') },
    { value: 'REJECTED', label: t('complaints.statuses.REJECTED') }
  ];

  const isSortMenuActive = useMemo(() => {
    const dirNonDefault = direction !== 'DESC';
    const statusActive = Array.isArray(status) && status.length > 0;
    return dirNonDefault || statusActive;
  }, [direction, status]);

  return (
    <div className="sort-menu-wrapper orders-sort-filters-wrapper" ref={menuRef}>
      <button
        type="button"
        className={`sort-menu-button${isSortMenuActive ? ' filter-active' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={t('orders.filters.sortButton')}
      >
        <span className="sort-menu-icon">
          <Icon name="sort-arrows" size={14} />
        </span>
        <span className="sort-menu-label">{t('orders.filters.sortButton')}</span>
        <span className="sort-menu-arrow">
          <Icon
            name="chevron-down"
            size={12}
            className={`expand-icon ${isOpen ? 'open' : ''}`}
          />
        </span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="sort-menu-dropdown orders-sort-filters-dropdown"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <div className="orders-sort-main-grid" style={{ padding: '0 8px' }}>
              <div className="sort-menu-section" style={{ borderRight: '1px solid var(--border-color)' }}>
                <div className="sort-menu-section-title" style={{ paddingLeft: '4px', marginBottom: '12px' }}>{t('orders.filters.sortBy')}</div>
                <div className="sort-menu-options" style={{ gap: '8px' }}>
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${sort === option.value ? 'active' : ''}`}
                      onClick={() => handleSortSelect(option.value)}
                      style={{ 
                        border: '1px solid var(--border-color)',
                        background: sort === option.value ? 'var(--nav-active-bg, rgba(59, 130, 246, 0.05))' : 'var(--bg-secondary)',
                        padding: '10px 12px',
                        justifyContent: 'flex-start'
                      }}
                    >
                      <span className="sort-menu-option-label" style={{ fontWeight: 600 }}>{option.label}</span>
                      {sort === option.value && (
                        <span className="sort-menu-option-check">
                          <Icon name="check" size={14} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
 
              <div className="sort-menu-section">
                <div className="sort-menu-section-title" style={{ paddingLeft: '4px', marginBottom: '12px' }}>{t('orders.filters.sortDirection')}</div>
                <div className="sort-menu-options" style={{ gap: '8px' }}>
                  {directionOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`sort-menu-option ${direction === option.value ? 'active' : ''}`}
                      onClick={() => handleDirectionSelect(option.value)}
                      style={{ 
                        border: '1px solid var(--border-color)',
                        background: direction === option.value ? 'var(--nav-active-bg, rgba(59, 130, 246, 0.05))' : 'var(--bg-secondary)',
                        padding: '10px 12px',
                        justifyContent: 'flex-start'
                      }}
                    >
                      <span className="sort-menu-option-icon" style={{ fontSize: '18px', marginRight: '4px' }}>{option.icon}</span>
                      <span className="sort-menu-option-label" style={{ fontWeight: 600 }}>{option.label}</span>
                      {direction === option.value && (
                        <span className="sort-menu-option-check">
                          <Icon name="check" size={14} />
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
 
            <div className="extra-filters-accordion">
              <button
                type="button"
                className="orders-extra-filters-toggle"
                onClick={() => setShowExtraFilters(!showExtraFilters)}
                aria-expanded={showExtraFilters}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon name="sliders" size={15} />
                  {t('orders.filters.extraFilters')}
                </span>
                <span className="orders-extra-filters-toggle-icon">
                  <Icon name="chevron-down" size={16} />
                </span>
              </button>

              <div className={`orders-extra-filters-body ${showExtraFilters ? 'open' : ''}`}>
                <div>
                  <div style={{ padding: '16px' }}>
                    <div className="more-filters-content">
                      <div className="more-filters-item" style={{ gridColumn: 'span 2' }}>
                        <label className="more-filters-label" style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px', display: 'block' }}>
                          {t('complaints.filters.allStatuses')}
                        </label>
                        <div className="status-multi-select-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                          {statusOptionsList.map(opt => (
                            <label key={opt.value} className={`more-filters-checkbox-label ${(status || []).includes(opt.value) ? 'active' : ''}`} style={{ 
                              margin: 0, 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '10px', 
                              padding: '10px 12px',
                              borderRadius: '10px',
                              border: '1.5px solid var(--border-color)',
                              background: (status || []).includes(opt.value) ? 'var(--nav-active-bg, rgba(59, 130, 246, 0.05))' : 'var(--bg-secondary)',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}>
                              <input
                                type="checkbox"
                                checked={(status || []).includes(opt.value)}
                                onChange={() => handleStatusToggle(opt.value)}
                                className="more-filters-checkbox"
                                style={{ 
                                  width: '18px', 
                                  height: '18px', 
                                  cursor: 'pointer',
                                  accentColor: 'var(--accent-primary)'
                                }}
                              />
                              <span className="more-filters-checkbox-text" style={{ fontWeight: 600, color: (status || []).includes(opt.value) ? 'var(--accent-primary)' : '#475569' }}>
                                {opt.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ComplaintsSortFiltersMenu;
